/**
 * Formatting utilities used across the application.
 */
import { CURRENCY_LOCALE, CURRENCY_MIN_DIGITS, BASE_CURRENCY } from '../constants';

// ── Currency ──────────────────────────────────────────────────────────────────

/**
 * Format a number as KES currency.
 * @param {number|string} amount
 * @returns {string} e.g. "KES 1,250.00"
 */
export function fmtKES(amount) {
  return `${BASE_CURRENCY} ${Number(amount || 0).toLocaleString(CURRENCY_LOCALE, {
    minimumFractionDigits: CURRENCY_MIN_DIGITS,
    maximumFractionDigits: CURRENCY_MIN_DIGITS,
  })}`;
}

/**
 * Format a number as a foreign currency.
 * @param {number} amount
 * @param {string} currency e.g. 'USD'
 * @param {string} symbol e.g. '$'
 */
export function fmtCurrency(amount, currency = 'USD', symbol = '') {
  const formatted = Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? `${symbol}${formatted}` : `${currency} ${formatted}`;
}

// ── Dates & Times ─────────────────────────────────────────────────────────────

/** Today's date as YYYY-MM-DD (local time). */
export const todayISO = () => new Date().toISOString().slice(0, 10);

/** Current month as YYYY-MM. */
export const currentMonth = () => new Date().toISOString().slice(0, 7);

/**
 * Format an ISO datetime string for display.
 * @param {string} iso  e.g. "2025-06-01T14:32:00Z"
 * @param {object} opts Intl.DateTimeFormat options
 */
export function fmtDatetime(iso, opts = {}) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(CURRENCY_LOCALE, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...opts,
  });
}

/** Format ISO datetime as time only: "14:32". */
export function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(CURRENCY_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format ISO date as date only: "1 Jun 2025". */
export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(CURRENCY_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Human-readable "time ago": "3 minutes ago", "2 days ago". */
export function timeAgo(iso) {
  if (!iso) return '';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60)    return `${seconds}s ago`;
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// ── Text ──────────────────────────────────────────────────────────────────────

/** Capitalise the first letter of a string. */
export const capitalise = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

/** Convert snake_case or kebab-case to Title Case. */
export const snakeToTitle = (s) =>
  (s || '')
    .replace(/[-_]/g, ' ')
    .replace(/\w\S*/g, capitalise);

/** Truncate a string to maxLength, appending "…". */
export const truncate = (s, maxLength = 40) =>
  s && s.length > maxLength ? `${s.slice(0, maxLength - 1)}…` : (s || '');

// ── Numbers ───────────────────────────────────────────────────────────────────

/** Format a large number with K/M suffix: 1500 → "1.5K". */
export function fmtCompact(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Sum an array of objects by a numeric key. */
export const sumBy = (arr, key) =>
  (arr || []).reduce((s, item) => s + parseFloat(item[key] || 0), 0);

/**
 * Returns a filesystem-safe slug of the current business name from branding config.
 * Falls back to 'GrandMemories' if branding is not yet loaded.
 * Used to prefix all downloaded file names so they reflect the actual hotel name.
 */
export const DEFAULT_HOTEL_NAME = 'Grand Memories Resort & Spa';

export function getBrandingSlug() {
  try {
    const raw = localStorage.getItem('grand_theme_v4') || localStorage.getItem('grand_theme_v2') || localStorage.getItem('grand_theme_v1');
    if (raw) {
      const t = JSON.parse(raw);
      const name = t.hotelName || DEFAULT_HOTEL_NAME;
      return name.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }
  } catch {}
  return DEFAULT_HOTEL_NAME.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

/** Returns the live hotel name (display form) from branding, never a hardcoded string. */
export function getBrandingName() {
  try {
    const raw = localStorage.getItem('grand_theme_v4') || localStorage.getItem('grand_theme_v2') || localStorage.getItem('grand_theme_v1');
    if (raw) {
      const t = JSON.parse(raw);
      return t.hotelName || DEFAULT_HOTEL_NAME;
    }
  } catch {}
  return DEFAULT_HOTEL_NAME;
}
